import type { HomeCopy } from '../content/home'
import Icon, { type IconName } from './Icon'
import styles from './PhoneMock.module.css'

/**
 * Today, drawn rather than screenshotted.
 *
 * A real capture would be a 600 px PNG that goes stale every release and cannot pick up
 * the visitor's language. This is the same layout in markup, so both languages get it
 * and it stays sharp. It follows the app's Today page: the day, the last reading as one
 * large serif number, the factual summary, the day strip, the entries, the quick tiles,
 * the three tabs.
 *
 * Nothing here is coloured by value, because nothing in the app is. Every dot on the
 * strip is the same ink.
 *
 * The whole device is hidden from assistive technology and the caption underneath does
 * the describing: read aloud, a list of bare times and numbers is noise, and the caption
 * is the sentence a person would actually want.
 */

/** Where the four readings sit: x across the day, y as height above the axis. */
const READINGS = [
  { x: 30, y: 24 },
  { x: 52, y: 38 },
  { x: 72, y: 30 },
  { x: 90, y: 27 },
]

const QUICK: { icon: IconName; key: keyof HomeCopy['mock']['quick'] }[] = [
  { icon: 'glucose', key: 'glucose' },
  { icon: 'insulin', key: 'insulin' },
  { icon: 'carbs', key: 'carbs' },
  { icon: 'note', key: 'note' },
]

export default function PhoneMock({ copy }: { copy: HomeCopy }) {
  const m = copy.mock

  return (
    <figure className={styles.figure}>
      <div className={styles.device} aria-hidden="true">
        <div className={styles.statusBar}>
          <span className={styles.statusTime}>{m.time}</span>
          <span className={styles.statusIcons}>
            <span className={styles.signal} />
            <span className={styles.battery}>
              <span className={styles.batteryFill} />
            </span>
          </span>
        </div>

        <div className={styles.dayRow}>
          <span className={styles.day}>
            {m.day}
            <br />
            {m.date}
          </span>
          <span className={styles.cog}>
            <Icon name="settings" size={20} />
          </span>
        </div>

        <div className={styles.hero}>
          <span className={styles.heroValue}>
            <span className={styles.value}>{m.value}</span>
            <span className={styles.unit}>{m.unit}</span>
          </span>
          <span className={styles.heroTime}>{m.time}</span>
        </div>

        <p className={styles.summary}>{m.summary}</p>

        <div className={styles.strip}>
          <span className={styles.stripBand} />
          <span className={styles.stripAxis} />
          {READINGS.map((point) => (
            <span
              key={point.x}
              className={styles.dot}
              style={{ left: `${point.x}%`, bottom: `${point.y}px` }}
            />
          ))}
        </div>
        <div className={styles.hours}>
          <span>00</span>
          <span>06</span>
          <span>12</span>
          <span>18</span>
          <span>24</span>
        </div>

        <div className={styles.rows}>
          {m.rows.map((row) => (
            <div key={row.time} className={styles.row}>
              <span className={styles.rowTime}>{row.time}</span>
              <span className={row.serif ? styles.rowTextSerif : styles.rowText}>{row.text}</span>
            </div>
          ))}
        </div>

        <div className={styles.quick}>
          {QUICK.map(({ icon, key }) => (
            <div key={key} className={styles.tile}>
              <Icon name={icon} size={19} />
              <span className={styles.tileLabel}>{m.quick[key]}</span>
            </div>
          ))}
        </div>

        <div className={styles.tabs}>
          <div className={`${styles.tab} ${styles.tabActive}`}>
            <Icon name="today" size={20} />
            <span>{m.tabs.today}</span>
          </div>
          <div className={styles.tab}>
            <Icon name="history" size={20} />
            <span>{m.tabs.history}</span>
          </div>
          <div className={styles.tab}>
            <Icon name="settings" size={20} />
            <span>{m.tabs.settings}</span>
          </div>
        </div>

        <div className={styles.homeBar}>
          <span />
        </div>
      </div>

      <figcaption className={styles.caption}>{copy.phoneCaption}</figcaption>
    </figure>
  )
}
